import { useMemo } from "react";
import type { BlockDoc, EditorConfig } from "./types";
import { ensureDoc } from "./utils";
import { BlockTypeProvider } from "./context";

/**
 * Read-only renderer. Used by both the public news read view and the dashboard
 * read preview. Falls back to the HTML snapshot when the doc shape isn't
 * recognised — keeps old/legacy article bodies viewable.
 *
 * Wraps its output in `<BlockTypeProvider>` so container blocks (Row in
 * particular) can dispatch their nested child blocks back through the type
 * registry without an explicit prop dance.
 */
export function BlockRenderer({
  doc,
  config,
  htmlFallback,
  className,
}: {
  doc: BlockDoc | unknown;
  config: EditorConfig;
  /** If the doc has no recognised blocks, render this HTML instead. */
  htmlFallback?: string;
  className?: string;
}) {
  const safeDoc = useMemo(() => ensureDoc(doc), [doc]);
  const types = useMemo(
    () => new Map(config.blockTypes.map((t) => [t.type, t])),
    [config.blockTypes],
  );

  if (safeDoc.blocks.length === 0) {
    if (htmlFallback) {
      return (
        <div
          className={`article-prose ${className ?? ""}`}
          dangerouslySetInnerHTML={{ __html: htmlFallback }}
        />
      );
    }
    return null;
  }

  return (
    <BlockTypeProvider config={config}>
      <div className={`article-prose ${className ?? ""}`}>
        {safeDoc.blocks.map((b) => {
          const t = types.get(b.type);
          if (!t) return null;
          const { Render } = t;
          return <Render key={b.id} data={b.data} inEditor={false} />;
        })}
      </div>
    </BlockTypeProvider>
  );
}
